import type { Tag, TagGroupDef } from '../types'

/**
 * Mock tags.
 * These will come from the Supabase `tags` table later; tags in the
 * 'source' group are generated automatically from Miro frame names.
 */
export const MOCK_TAGS: Tag[] = [
  // Category
  { id: 'beverage', name: 'Beverage', group: 'category' },
  { id: 'beauty', name: 'Beauty & Care', group: 'category' },
  { id: 'food', name: 'Food', group: 'category' },
  { id: 'spirits', name: 'Spirits', group: 'category' },
  { id: 'household', name: 'Household', group: 'category' },
  { id: 'supplement', name: 'Supplement', group: 'category' },

  // Structure
  { id: 'bottle', name: 'Bottle', group: 'structure' },
  { id: 'can', name: 'Can', group: 'structure' },
  { id: 'carton', name: 'Carton', group: 'structure' },
  { id: 'pouch', name: 'Pouch', group: 'structure' },
  { id: 'jar', name: 'Jar', group: 'structure' },
  { id: 'tube', name: 'Tube', group: 'structure' },
  { id: 'rigid-box', name: 'Rigid Box', group: 'structure' },

  // Material
  { id: 'glass', name: 'Glass', group: 'material' },
  { id: 'aluminum', name: 'Aluminum', group: 'material' },
  { id: 'paperboard', name: 'Paperboard', group: 'material' },
  { id: 'pcr-plastic', name: 'PCR Plastic', group: 'material' },
  { id: 'mono-film', name: 'Mono-material Film', group: 'material' },

  // Finish / surface
  { id: 'matte', name: 'Matte', group: 'finish' },
  { id: 'gloss', name: 'Gloss', group: 'finish' },
  { id: 'soft-touch', name: 'Soft Touch', group: 'finish' },
  { id: 'foil', name: 'Foil Stamp', group: 'finish' },
  { id: 'emboss', name: 'Emboss', group: 'finish' },

  // Style
  { id: 'minimal', name: 'Minimal', group: 'style' },
  { id: 'editorial', name: 'Editorial', group: 'style' },
  { id: 'retro', name: 'Retro', group: 'style' },
  { id: 'playful', name: 'Playful', group: 'style' },
  { id: 'premium', name: 'Premium', group: 'style' },
  { id: 'maximal', name: 'Maximal', group: 'style' },

  // Source frames (created automatically by the future import pipeline)
  { id: 'frame-beverage-audit-2025', name: '2025 Beverage Audit', group: 'source' },
  { id: 'frame-beauty-shelf', name: 'Beauty Shelf Study', group: 'source' },
  { id: 'frame-sustainable-structures', name: 'Sustainable Structures', group: 'source' },
  { id: 'frame-premium-cues', name: 'Premium Cues', group: 'source' },
  { id: 'frame-retro-revival', name: 'Retro Revival', group: 'source' },
  { id: 'frame-color-blocking', name: 'Color Blocking Study', group: 'source' },
]

export const TAG_GROUP_LABELS: Record<Tag['group'], string> = {
  'packaging-format': 'Packaging Format',
  product: 'Product',
  brand: 'Brand',
  industry: 'Industry',
  function: 'Function',
  market: 'Market',
  other: 'Other',
  system: 'System',
  category: 'Category',
  structure: 'Structure',
  material: 'Material',
  finish: 'Finish',
  style: 'Style',
  source: 'Source Frame',
}

/**
 * The order the filter shows its categories in.
 *
 * Brand and Function are listed although nothing carries them yet: they are
 * part of the scheme, and an empty row says "this exists, fill it" where a
 * missing row says nothing at all. A group that is not on this list still
 * renders, after these — that is what keeps the mock data usable.
 */
export const TAG_GROUP_ORDER: Tag['group'][] = [
  'packaging-format',
  'product',
  'brand',
  'industry',
  'function',
  'market',
  'other',
]

/** Never rendered as a chip. See PackagingMode. */
export const HIDDEN_TAG_GROUPS: Tag['group'][] = ['system']

/** The tag that divides the library in two. */
export const NON_PACKAGING_SLUG = 'non-packaging'

/**
 * The categories, for offline work.
 *
 * The real list is the `tag_groups` table; this stands in when the app runs on
 * local files, and is also what seeded that table. Built from the two
 * constants above rather than written out again, so the three cannot drift.
 */
export const MOCK_TAG_GROUPS: TagGroupDef[] = [
  ...TAG_GROUP_ORDER.map((slug, index) => ({
    slug,
    label: TAG_GROUP_LABELS[slug] ?? slug,
    position: (index + 1) * 10,
    hidden: false,
  })),
  ...HIDDEN_TAG_GROUPS.map((slug) => ({
    slug,
    label: TAG_GROUP_LABELS[slug] ?? slug,
    position: 999,
    hidden: true,
  })),
]
